import * as date from "shared/date"
import * as money from "shared/money"

// NOTE: there should be NO ROUNDING of any numbers in this file
// any rounding should occur at the last minute, outside of this file

// NOTE: to simplify things for consumers of this file, we will have public functions take annual interest rates,
// and private functions take monthly interest rates.

const removeDecimals = (v) => v.split(".")[0]

export const asCurrency = (v) => "$" + removeDecimals(money.format(v))
export const asCurrencyWithDecimals = (v) => "$" + money.format(parseFloat(v).toFixed(2))

export const wholeNumberCheck = (v) => {
  const n = v.toFixed(2)
  return Number.isInteger(Number(n)) ? asCurrency(n) : asCurrencyWithDecimals(v)
}

export const applyMonthlyInterest = (annualRate, balance) => balance * (1 + annualRate / 12)

export const calcMonthlyRate = (rate) => rate / 12

export const calcCreditCardMinimum = (balance, percent) => (balance * percent > 25 ? balance * percent : 25)

export const reduceOneMonthMin = (balance, annualRate, percent) => balance * (calcMonthlyRate(annualRate) + 1) - calcCreditCardMinimum(balance, percent)

const reduceOneMonthFixed = (balance, monthlyRate, payment) => balance * (monthlyRate + 1) - payment

export const getMonthsToPayoffMinimum = (balance, annualRate, percent) => {
  let months = 0
  let oldBalance = balance
  let newBalance = null
  while (oldBalance > 0 && months < 900) {
    newBalance = reduceOneMonthMin(oldBalance, annualRate, percent)
    months++
    if (newBalance > oldBalance) {
      return 120
    } else {
      oldBalance = newBalance
    }
  }
  return months
}

export const calcTotalInterestPaidCreditCard = (balance, annualRate, percent) => {
  const months = getMonthsToPayoffMinimum(balance, annualRate, percent)
  const tup = [...Array(months)].reduce(
    (memo, months) => {
      const [b, p, t] = memo
      const newBalance = reduceOneMonthMin(b, annualRate, percent)
      const newPayment = calcCreditCardMinimum(newBalance, percent)
      memo[0] = newBalance
      memo[1] = newPayment
      memo[2] = t + newPayment
      return memo
    },
    [balance, balance * percent, 0]
  )
  // => [current balance, payment for the month, running total of payments]
  return Math.max(0, tup[2] - balance)
}

/*
 *  Produces a payment schedule given a reducer thunk.
 *
 *  reduceOneMonthMin has the following signature:
 *    (prevBalance) => reduceOneMonthMin(prevBalance, rate, ...)
 */
const genPaymentSchedule = (balance, reduceOneMonthFn, periods) =>
  [...Array(periods)].reduce(
    (memo, x, idx) => {
      const prevBalance = memo[memo.length - 1]
      memo.push(Math.max(0, reduceOneMonthFn(prevBalance)))
      return memo
    },
    [balance]
  )

export const genMinimumPaymentSchedule = (balance, annualRate, initialMinimumPayment) => {
  const percent = initialMinimumPayment / balance
  return genPaymentSchedule(balance, (prevBalance) => reduceOneMonthMin(prevBalance, annualRate, percent), getMonthsToPayoffMinimum(balance, annualRate, percent))
}

// for Fixed loan with a zero monthlyRate
const calcMonthsToPayoffWithZeroInterest = (balance, monthlyRate, payment) => Math.ceil(balance / payment)

// for Fixed loan with a monthlyRate
const calcMonthsToPayoffWithInterest = (balance, monthlyRate, payment) =>
  Math.ceil(Math.log(payment) / Math.log(monthlyRate + 1) - Math.log(payment - balance * monthlyRate) / Math.log(monthlyRate + 1))

// we are rounding here, as the number of months should not be a decimal
export const calcMonthsToPayoffFixedLoan = (balance, monthlyRate, payment) =>
  monthlyRate === 0 ? calcMonthsToPayoffWithZeroInterest(balance, monthlyRate, payment) : calcMonthsToPayoffWithInterest(balance, monthlyRate, payment)

export const genFixedPaymentSchedule = (balance, annualRate, payment) =>
  genPaymentSchedule(
    balance,
    (prevBalance) => reduceOneMonthFixed(prevBalance, calcMonthlyRate(annualRate), payment),
    calcMonthsToPayoffFixedLoan(balance, calcMonthlyRate(annualRate), payment)
  )

/* Calculates interest paid for a loan given a fixed payment. */
export const calcTotalInterestPaid = (balance, annualRate, payment) => {
  const months = calcMonthsToPayoffFixedLoan(balance, calcMonthlyRate(annualRate), payment)
  const totalPaid = payment * months
  return totalPaid - balance
}

export const sum = (vs, k) => {
  const _sum = (vs) => vs.reduce((m, v) => m + v, 0)
  if (k) {
    return _sum(vs.map((o) => o[k]))
  } else {
    return _sum(vs)
  }
}

const mostRecentBalances = (list) => list[list.length - 1]

export const calcTotal = (items) => sum(items.map((i) => i.value))

export const calcNewTotal = (items) => sum(items.map((i) => i.newValue))

export const calcTotalBalance = (debts) => sum(debts.map((d) => d.balance))

const hasRemaining = (list) => {
  return list.length === 0 || sum(mostRecentBalances(list)) > 0
}

const applyInterest = (debts, balances) => balances.map((b, idx) => applyMonthlyInterest(debts[idx].rate, b))

const getMinimumPaymentPercentage = (initialBal, initialMin) => initialMin / initialBal

const getPayment = (debt, bal) => {
  const {type, balance, payment} = debt
  let p
  if (type === "credit") {
    p = calcCreditCardMinimum(bal, getMinimumPaymentPercentage(balance, payment))
  } else {
    p = payment
  }
  return p
}

const calcExtra = (x, payments) => x - payments.reduce((m, p) => m + p, 0)

// expects rate to be an annual rate, and the payment to be fixed
// this also assumes periods are months, hence dividing the rate by 12
// makes sure the amount the debt is reduced each period comes out positive
export const canBePaidOff = ({balance, rate, payment}) => payment - balance * (rate / 12) > 0

const getPayoffMonth = (schedule, idx) => schedule.map((bals) => bals[idx]).indexOf(0)

const getPayoffDates = (debts, schedule) => debts.map((d, idx) => date.monthsFromNow(getPayoffMonth(schedule, idx)))

/* Hypothetical interest payments given a normal minimum payment schedule */
const interestPaidOnDebt = ({balance, rate, type, payment}) => {
  const annualRate = rate // update the name, to avoid confusion
  let r
  if (type === "credit") {
    r = calcTotalInterestPaidCreditCard(balance, annualRate, getMinimumPaymentPercentage(balance, payment), payment)
  } else {
    r = calcTotalInterestPaid(balance, annualRate, payment)
  }
  return r
}

const mightHaveInterestPaid = (debts) => debts.reduce((memo, debt) => memo + interestPaidOnDebt(debt), 0)

const interestPaidList = (debts) => debts.map((d) => interestPaidOnDebt(d))

const calcTotalPayments = (debts) => sum(debts, "payment")

const bySmallestBalance = (debts) => debts.concat().sort((a, b) => (a.balance > b.balance ? 1 : -1))

const byHighestRate = (debts) =>
  debts.concat().sort((a, b) => {
    if (a.rate > b.rate) {
      return -1
    } else {
      if (a.rate === b.rate) {
        return a.balance > b.balance ? 1 : -1
      } else {
        return 1
      }
    }
  })

const methodIdx = {
  snowball: bySmallestBalance,
  avalanche: byHighestRate
}

export const orderBy = (method, debts) => methodIdx[method](debts)

const recalculateBalances = (balances, payments) => {
  return payments.map((p, idx) => {
    return balances[idx] - p
  })
}

export const calcSnowball = (debts, additionalPayment) => {
  const x = calcTotalPayments(debts) + additionalPayment
  let n = 0
  let interest = 0
  const initialBalances = debts.map((d) => d.balance)
  const initialPayments = debts.map((d) => d.payment)
  let schedule = [initialBalances]

  while (hasRemaining(schedule)) {
    let beforeInterest, balances, minimums
    if (n === 0) {
      beforeInterest = initialBalances
      balances = beforeInterest
      minimums = initialPayments
    } else {
      beforeInterest = mostRecentBalances(schedule)
      balances = applyInterest(debts, beforeInterest)
      minimums = balances.map((b, idx) => getPayment(debts[idx], b))
    }

    interest = interest + sum(balances) - sum(beforeInterest)

    const payments = minimums.reduce((memo, p, idx) => {
      memo[idx] = Math.min(balances[idx], p)
      return memo
    }, minimums)

    const _balances = recalculateBalances(balances, payments)

    // Handling breakpoints in the debt down schedule
    const _payments = payments.reduce((memo, p, idx) => {
      memo[idx] = Math.min(balances[idx], p + calcExtra(x, memo))
      return memo
    }, payments)

    schedule.push(recalculateBalances(balances, _payments))
    n++
  }

  return {
    schedule,
    payoffDates: getPayoffDates(debts, schedule), // TODO: Fix this function to not assume
    interestPaid: interest,
    interestSaved: Math.max(0, mightHaveInterestPaid(debts) - interest)
  }
}

/* Functions for Compound interest */

// the math here is based off this formula:
// [ P(1+r/n)^(nt) ] + [ PMT × (((1 + r/n)^(nt) - 1) / (r/n)) × (1+r/n) ]
// PMT(1+i)((1+i)^N - 1)/i
// from this website: https://www.thecalculatorsite.com/articles/finance/compound-interest-formula.php?page=2

// A = the future value of the investment/loan, including interest
// P = the principal investment amount (the initial deposit or loan amount)
// PMT = the monthly payment
// r = the annual interest rate (decimal)
// n = the number of times that interest is compounded per unit t
// t = the time (months, years, etc) the money is invested or borrowed for

export const amountWithCompoundInterest = (principal, years, rate) => (rate > 0 ? principal * Math.pow(1 + rate / 12, 12 * years) : principal)

export const amountWithCompoundInterestAnnual = (principal, years, rate) => (rate > 0 ? principal * Math.pow(1 + rate / 1, years * 1) : principal)

// this is for monthly contributions
export const amountWithCompoundInterestOnContributions = (contribution, years, rate) =>
  rate > 0 ? contribution * ((Math.pow(1 + rate / 12, 12 * years) - 1) / (rate / 12)) * (1 + rate / 12) : contribution * years * 12

export const amountWithCompoundInterestOnContributionsAnnual = (contribution, years, rate) =>
  rate > 0 ? contribution * ((Math.pow(1 + rate / 1, years * 1) - 1) / (rate / 1)) * (1 + rate / 1) : contribution * years

export const interestEarnedOnPrincipal = (principal, years, rate) => amountWithCompoundInterest(principal, years, rate) - principal

export const interestEarnedOnPrincipalAnnual = (principal, years, rate) => amountWithCompoundInterestAnnual(principal, years, rate) - principal

export const interestEarnedOnContribution = (contribution, years, rate) => amountWithCompoundInterestOnContributions(contribution, years, rate) - contribution * 12 * years

export const interestEarnedOnContributionAnnual = (contribution, years, rate) => amountWithCompoundInterestOnContributionsAnnual(contribution, years, rate) - contribution * years

export const getTotalWithCompoundInterest = (principal, contribution, years, rate) =>
  amountWithCompoundInterest(principal, years, rate) + amountWithCompoundInterestOnContributions(contribution, years, rate)

export const getCompoundInterest = (principal, contribution, years, rate) =>
  rate > 0 ? interestEarnedOnPrincipal(principal, years, rate) + interestEarnedOnContribution(contribution, years, rate) : 0

/* Functions for Simple interest*/
export const simpleInterest = (principal, years, rate) => principal * rate * years

export const amountWithSimpleInterestPrincipal = (principal, years, rate) => simpleInterest(principal, years, rate) + principal

export const calcMonthlyPayment = (balance, years, rate) => {
  const r = calcMonthlyRate(rate)
  return r === 0 ? balance / years / 12 : (balance * r) / (1 - Math.pow(1 / (1 + r), years * 12))
}

// NOTE: simple interest calculations on monthly contributions are essentially just a hypothetical exercise. As such the below formulas are incorrect/irrelevant, and are out of use for now

// const amountWithSimpleInterestContributions = (contribution, years, rate) => (
//   simpleInterest(contribution * 12, years, rate) + (contribution * 12 * years)
// )

// export const getTotalWithSimpleInterest = (principal, contribution, years, rate) => (
//   amountWithSimpleInterestPrincipal(principal, years, rate) + amountWithSimpleInterestContributions(contribution, years, rate)
// )

// export const getSimpleInterest = (principal, contribution, years, rate) => {
//   const contributions = years > 0 ? contribution * 12 : 0
//   return simpleInterest(principal, years, rate) + simpleInterest(contributions, years, rate)
// }
